import { useRef } from 'react';
import { motion, useInView } from 'motion/react';
import { BreakLine } from '../components/ornaments/BreakLine';
import { AppBar } from '../components/home/AppBar';
import { HeroSection } from '../components/home/HeroSection';
import { TwitchPromo } from '../components/home/TwitchPromo';
import { JoinCallout } from '../components/home/JoinCallout';
import { CallToRise } from '../components/home/CallToRise';
import { Partners } from '../components/home/Partners';
import { PromoBanner, PROMO_BANNER_ART } from '../components/home/PromoBanner';
import { HostsTriangle, HOSTS_TRIANGLE_ART } from '../components/neon/HostsTriangle';
import { HeroesGrid } from '../components/neon/HeroesGrid';
import { ProductsGrid } from '../components/neon/ProductsGrid';
import { cellGridArtSrcs } from '../components/neon/CellGrid';
import { HEROES, PRIZES, watchUrlForPhase } from '../content/site';
import { useEventState } from '../lib/eventState';
import { fadeRise, revealViewport, useMotionEnabled } from '../lib/motion';
import { useArtReady } from '../lib/useArtReady';
import { useScrolledPast } from '../lib/useScrolledPast';
import { useSectionView } from '../lib/useSectionView';

// Art each section needs on screen before its reveal plays (static content →
// computed once at module load).
const HOSTS_ART = [HOSTS_TRIANGLE_ART];
const HEROES_ART = cellGridArtSrcs(HEROES);
const PRIZES_ART = cellGridArtSrcs(PRIZES);

function Reveal({
  children,
  className,
  artSrcs,
  track,
}: {
  children: React.ReactNode;
  className?: string;
  /** The section reveals only after this art has loaded + decoded (with a
   *  safety timeout in useArtReady) — a reveal that plays over unloaded art
   *  spends its one entrance on an empty frame. */
  artSrcs?: readonly string[];
  /** Section slug for Plausible's `Section View` (scroll reach). Omit for
   *  wrappers that aren't a section in their own right. Measured off the same
   *  element as the reveal but on its OWN observer — the reveal is art-gated
   *  and motion-gated, and neither should move the scroll-depth number. */
  track?: string;
}) {
  // Reduced motion → content renders in place, no reveal choreography.
  const motionOn = useMotionEnabled();
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, amount: revealViewport.amount });
  const ready = useArtReady(artSrcs ?? []);
  useSectionView(ref, track);
  if (!motionOn) {
    // Still ref'd: reduced-motion visitors' scroll depth counts too.
    return (
      <div ref={ref} className={className}>
        {children}
      </div>
    );
  }
  // `inView` latches (once: true) and `ready` only flips false→true, so the
  // reveal still fires exactly once — now art-aware.
  return (
    <motion.div
      ref={ref}
      variants={fadeRise}
      initial="hidden"
      animate={inView && ready ? 'visible' : 'hidden'}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/** The hosts section's red ambient halo blooms in as the section approaches
 *  instead of popping with the page — pure opacity on a CSS gradient. */
function HaloBloom({ className, background }: { className: string; background: string }) {
  const motionOn = useMotionEnabled();
  if (!motionOn) {
    return <div aria-hidden="true" className={className} style={{ background }} />;
  }
  return (
    <motion.div
      aria-hidden="true"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 1.4, ease: 'easeOut' }}
      className={className}
      style={{ background }}
    />
  );
}

export default function HomePage() {
  const [event] = useEventState();
  const phase = event?.phase ?? 'PRE';
  const showPrizes = phase === 'LIVE' || phase === 'POST';
  // Everything below the hero waits for the phase to resolve (cached in
  // sessionStorage after the first fetch, so this only defers a cold visit
  // by the /event/state round trip). Rendering the PRE arrangement as a
  // guess meant a cold LIVE visit re-shuffled the whole band mid-paint.
  const phaseKnown = event !== null;
  // AppBar trigger: the hero's state element (timer / LIVE NOW / WATCH).
  // Mounted here — outside every Reveal — so no transformed ancestor turns
  // the bar's position:fixed into a local containing block.
  const heroStateRef = useRef<HTMLDivElement>(null);
  const pastHeroState = useScrolledPast(heroStateRef);

  return (
    <div className="flex w-full flex-col items-center overflow-hidden">
      <AppBar event={event} visible={pastHeroState} heroAnchor={heroStateRef} />
      <HeroSection event={event} stateRef={heroStateRef} />

      {phaseKnown && (
        <>
          {/* Section 02 band — a FIXED-height black fade blends the seam from
          the hero (7.17 frames: the texture is fully visible shortly below
          the hero edge; a proportional fade scaled with band content and
          swallowed whatever led the band). PRE/POST lead with the Join
          callout, so they get extra top padding to float the black frame
          clear of the seam, per the mocks. The red ambient halo lives
          INSIDE the hosts section below, anchored to the triangle, so it
          stays behind the triangle in every phase. */}
          <div
            className={`relative flex w-full flex-col items-center gap-[clamp(2rem,5vw,4rem)] pb-0 ${
              phase === 'LIVE' ? 'pt-10' : 'pt-[clamp(3.5rem,7vw,6rem)]'
            }`}
          >
            <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
              <div className="absolute inset-x-0 top-0 h-[clamp(5rem,9vw,8rem)] bg-gradient-to-b from-black to-transparent" />
            </div>

            {/* "Join HyperX" callout — same copy on every state, phase-specific
            slot (7.17 frames): PRE above the divider, LIVE below it (after
            products), POST leading the band straight into products. */}
            {phase === 'POST' && (
              <Reveal className="w-full">
                <JoinCallout />
              </Reveal>
            )}

            {/* Live/post: product showcase leads the band, before the Twitch
            promo (dev frame order). */}
            {showPrizes && (
              <section aria-labelledby="prizes-heading" className="w-full max-w-content px-4">
                <h2 id="prizes-heading" className="sr-only">
                  Prizes
                </h2>
                <Reveal className="w-full" artSrcs={PRIZES_ART} track="prizes">
                  <ProductsGrid />
                </Reveal>
              </section>
            )}

            {phase === 'PRE' && (
              <Reveal className="w-full">
                <JoinCallout />
              </Reveal>
            )}
            <BreakLine />
            {phase === 'LIVE' && (
              <Reveal className="w-full">
                <JoinCallout />
              </Reveal>
            )}
            {/* Twitch promo band — PRE/POST only since the 7.27 dev frames
            (HX-36): the live page's Twitch CTA lives in the hero under
            LIVE NOW instead of down here. */}
            {phase !== 'LIVE' && (
              <Reveal className="w-full">
                {/* PRE deep-links to the scheduled segment (HX-44); POST
                follows the admin's watch URL, falling back to the channel.
                This band only renders on those two phases. */}
                <TwitchPromo href={watchUrlForPhase(phase, event.watchUrl)} />
              </Reveal>
            )}

            <section
              aria-labelledby="hosts-heading"
              className={`relative flex w-full flex-col items-center gap-[clamp(1rem,2vw,1.5rem)] pt-[clamp(2rem,6vw,6rem)] ${
                // With a banner below, THAT wrapper owns the whole transition
                // to the heroes grid — keeping this padding too would stack
                // two gaps and push the banner far off the triangle. Without
                // one, this is the original triangle→heroes gap, untouched.
                event.bannerVisible ? 'pb-0' : 'pb-[clamp(2rem,6vw,6rem)]'
              }`}
            >
              {/* Red ambient halo — CSS gradient (profile sampled from the
              designer's bg-mid.png: core rgb(119,28,28) over the
              rgb(24,24,24) texture) anchored to THIS section so it tracks
              the triangle in every phase. Placement is phase-specific per
              the dev frames: countdown centers it directly behind the
              triangle; live/post ride it up so the hot zone sits behind
              whatever leads this stretch (post: the Twitch promo; live since
              7.27: the join callout — the live Twitch CTA moved into the
              hero). Fades to transparent over the continuous tile, so no
              image-edge boundary can show. */}
              {/* The ellipse must reach zero alpha INSIDE the div (center_y −
              vertical radius ≥ 0) or its top edge clips as a hard line —
              the live/post box is oversized upward for exactly that.
              Live/post geometry keeps the original absolute placement:
              hot core 0.15×section above the section (behind the Twitch
              logo), vertical falloff radius 0.6×section. */}
              <HaloBloom
                className={`pointer-events-none absolute inset-x-0 -z-10 ${
                  showPrizes ? 'bottom-[-5%] top-[-85%]' : 'bottom-[-5%] top-[-10%]'
                }`}
                background={
                  showPrizes
                    ? 'radial-gradient(ellipse 47% 31.5% at 50% 37%, rgba(136, 29, 29, 0.85) 0%, ' +
                      'rgba(136, 29, 29, 0.4) 51%, rgba(136, 29, 29, 0.09) 84%, rgba(136, 29, 29, 0) 100%)'
                    : 'radial-gradient(ellipse 47% 46% at 50% 49%, rgba(136, 29, 29, 0.85) 0%, ' +
                      'rgba(136, 29, 29, 0.4) 51%, rgba(136, 29, 29, 0.09) 84%, rgba(136, 29, 29, 0) 100%)'
                }
              />
              <Reveal className="w-full max-w-content px-4" artSrcs={HOSTS_ART} track="hosts">
                <h2
                  id="hosts-heading"
                  className="mb-[clamp(-1rem,-2vw,-1.5rem)] text-center font-heading text-[clamp(1.15rem,1.9vw,2.25rem)] uppercase tracking-wide text-brand-white"
                >
                  Meet Your Hosts
                </h2>
                <HostsTriangle />
              </Reveal>
            </section>
          </div>

          {/* HP promo banner (HX-42) — under the hosts triangle in ALL three
          phases. The window is admin-configured and resolved server-side, so
          this is a straight boolean (HX-43).

          It sits OUTSIDE the section-02 band deliberately. Inside, it picked
          up the band's flex `gap` on top of the hosts section's own bottom
          padding while the heroes section below contributes `pt-0` — so the
          banner hugged "MEET YOUR HEROES" and floated far from the triangle.
          Out here the only spacing is what this wrapper declares.

          The NEGATIVE top margin is load-bearing, not a nudge. Composited
          over the page tone, the triangle art's solid content ends at 86% of
          its own box — the bottom 14% is a near-invisible shadow tail baked
          into the export. That tail alone is ~168px at 1440, already twice
          the gap below, so even zero top padding reads badly lopsided; the
          banner has to pull UP into the dead space. Re-measure the 86% if the
          triangle art is ever re-exported.

          The bottom pad matches the hosts section's own `pb`, which is the
          gap the triangle→heroes transition uses when no banner renders. */}
          {event.bannerVisible && (
            <Reveal
              className="-mt-[clamp(1.25rem,6.5vw,5.5rem)] flex w-full justify-center pb-[clamp(2rem,6vw,6rem)]"
              artSrcs={PROMO_BANNER_ART}
              track="promo-banner"
            >
              <PromoBanner />
            </Reveal>
          )}

          <section
            aria-labelledby="heroes-heading"
            className="flex w-full flex-col items-center gap-[clamp(1rem,2vw,1.5rem)] px-4 pt-0 pb-[clamp(2.5rem,6vw,6rem)]"
          >
            <Reveal className="w-full max-w-content" artSrcs={HEROES_ART} track="heroes">
              <h2
                id="heroes-heading"
                className="mb-[clamp(1rem,2vw,1.5rem)] text-center font-heading text-[clamp(1.15rem,1.9vw,2.25rem)] uppercase tracking-wide text-brand-white"
              >
                Meet Your Heroes
              </h2>
              <HeroesGrid />
            </Reveal>
          </section>

          <BreakLine withCenterStar />

          {/* Call to Rise is the page's last real content block, so reaching it
          is the closest thing this page has to a "read the whole thing"
          signal. Partners sits below it but is a logo row — not a section
          anyone scrolls FOR, so it isn't tracked separately.

          LIVE/POST render the WATCH NOW CTA inside the box (HX-48). Same
          destination as every other watch surface — the admin's single watch
          link, or the Twitch channel while it's blank (watchUrlForPhase). */}
          <Reveal className="w-full py-[clamp(1.5rem,3vw,3rem)]" track="call-to-rise">
            <CallToRise phase={phase} watchUrl={watchUrlForPhase(phase, event.watchUrl)} />
          </Reveal>

          <Reveal>
            <Partners />
          </Reveal>
        </>
      )}
    </div>
  );
}
